"use client";

import { c as _c } from "react/compiler-runtime";
import DropListItemContentMedia from "@/components/drops/view/item/content/media/DropListItemContentMedia";
import WaveDropAuthorPfp from "@/components/waves/drops/WaveDropAuthorPfp";
import WaveDropTime from "@/components/waves/drops/time/WaveDropTime";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import clsx from "clsx";
import React, { useEffect, useMemo, useRef, useState } from "react";
const SWIPE_TRIGGER_THRESHOLD = 96;
const MAX_SWIPE_OFFSET = 132;
const SWIPE_EXIT_DURATION_MS = 180;
const SWIPE_EXIT_OFFSET = 420;
interface MemesQuickVotePreviewProps {
  readonly drop: ExtendedDrop;
  readonly isBusy: boolean;
  readonly isMobile: boolean;
  readonly remainingCount: number;
  readonly swipeVoteAmount: number | null;
  readonly uncastPower: number | null;
  readonly votingLabel: string | null;
  readonly onAdvanceStart: () => void;
  readonly onSkip: () => void;
  readonly onVoteWithSwipe: () => void;
}
function MemesQuickVotePreviewContent(t0) {
  const $ = _c(95);
  const {
    drop,
    isBusy,
    isMobile,
    remainingCount,
    swipeVoteAmount,
    uncastPower,
    votingLabel,
    onAdvanceStart,
    onSkip,
    onVoteWithSwipe
  } = t0;
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [swipeExitDirection, setSwipeExitDirection] = useState(null);
  const swipeCommitTimeoutRef = useRef(null);
  const touchStartXRef = useRef(null);
  const touchStartYRef = useRef(null);
  const title = drop.metadata.find(_temp)?.data_value ?? "Untitled submission";
  const description = drop.metadata.find(_temp2)?.data_value ?? "";
  let t1;
  if ($[0] !== drop.parts) {
    t1 = drop.parts.at(0)?.media.at(0);
    $[0] = drop.parts;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const artworkMedia = t1;
  const authorLabel = drop.author.handle ?? drop.author.primary_address;
  let t2;
  bb0: {
    if (swipeVoteAmount === null) {
      t2 = null;
      break bb0;
    }
    let t3;
    if ($[2] !== swipeVoteAmount) {
      t3 = formatNumberWithCommas(swipeVoteAmount);
      $[2] = swipeVoteAmount;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    t2 = `${t3} ${votingLabel ?? "votes"}`;
  }
  const swipeHint = t2;
  let t3;
  if ($[4] !== swipeExitDirection) {
    t3 = () => {
      if (swipeExitDirection) {
        return;
      }
      setSwipeOffset(0);
      touchStartXRef.current = null;
      touchStartYRef.current = null;
    };
    $[4] = swipeExitDirection;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const resetSwipe = t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      if (swipeCommitTimeoutRef.current !== null) {
        window.clearTimeout(swipeCommitTimeoutRef.current);
        swipeCommitTimeoutRef.current = null;
      }
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const clearSwipeCommitTimeout = t4;
  let t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => () => {
      if (swipeCommitTimeoutRef.current !== null) {
        window.clearTimeout(swipeCommitTimeoutRef.current);
        swipeCommitTimeoutRef.current = null;
      }
    };
    t6 = [];
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  let t7;
  if ($[9] !== onAdvanceStart) {
    t7 = (direction, action) => {
      clearSwipeCommitTimeout();
      setSwipeExitDirection(direction);
      setSwipeOffset(direction === "left" ? -SWIPE_EXIT_OFFSET : SWIPE_EXIT_OFFSET);
      touchStartXRef.current = null;
      touchStartYRef.current = null;
      onAdvanceStart();
      swipeCommitTimeoutRef.current = window.setTimeout(() => {
        action();
      }, SWIPE_EXIT_DURATION_MS);
    };
    $[9] = onAdvanceStart;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const beginSwipeCommit = t7;
  let t8;
  if ($[11] !== isBusy || $[12] !== isMobile || $[13] !== swipeExitDirection) {
    t8 = event => {
      if (isBusy || !isMobile || swipeExitDirection || !event.touches[0]) {
        return;
      }
      touchStartXRef.current = event.touches[0].clientX;
      touchStartYRef.current = event.touches[0].clientY;
    };
    $[11] = isBusy;
    $[12] = isMobile;
    $[13] = swipeExitDirection;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const handleTouchStart = t8;
  let t9;
  if ($[15] !== isBusy || $[16] !== isMobile || $[17] !== swipeExitDirection) {
    t9 = event_0 => {
      if (isBusy || !isMobile || swipeExitDirection || touchStartXRef.current === null || touchStartYRef.current === null || !event_0.touches[0]) {
        return;
      }
      const deltaX = event_0.touches[0].clientX - touchStartXRef.current;
      const deltaY = event_0.touches[0].clientY - touchStartYRef.current;
      if (Math.abs(deltaY) > Math.abs(deltaX)) {
        return;
      }
      setSwipeOffset(Math.max(-MAX_SWIPE_OFFSET, Math.min(deltaX, MAX_SWIPE_OFFSET)));
    };
    $[15] = isBusy;
    $[16] = isMobile;
    $[17] = swipeExitDirection;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const handleTouchMove = t9;
  let t10;
  if ($[19] !== beginSwipeCommit || $[20] !== isBusy || $[21] !== isMobile || $[22] !== onSkip || $[23] !== onVoteWithSwipe || $[24] !== resetSwipe || $[25] !== swipeOffset || $[26] !== swipeVoteAmount) {
    t10 = () => {
      if (isBusy || !isMobile) {
        resetSwipe();
        return;
      }
      if (swipeOffset <= -SWIPE_TRIGGER_THRESHOLD) {
        beginSwipeCommit("left", onSkip);
        return;
      }
      if (swipeOffset >= SWIPE_TRIGGER_THRESHOLD && swipeVoteAmount !== null) {
        beginSwipeCommit("right", onVoteWithSwipe);
        return;
      }
      resetSwipe();
    };
    $[19] = beginSwipeCommit;
    $[20] = isBusy;
    $[21] = isMobile;
    $[22] = onSkip;
    $[23] = onVoteWithSwipe;
    $[24] = resetSwipe;
    $[25] = swipeOffset;
    $[26] = swipeVoteAmount;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const handleTouchEnd = t10;
  let cardTransform;
  if (isMobile) {
    if (swipeExitDirection === "left") {
      cardTransform = `translateX(-${SWIPE_EXIT_OFFSET}px) rotate(-6deg)`;
    } else {
      if (swipeExitDirection === "right") {
        cardTransform = `translateX(${SWIPE_EXIT_OFFSET}px) rotate(6deg)`;
      } else {
        cardTransform = `translateX(${swipeOffset}px)`;
      }
    }
  }
  let t11;
  if ($[28] !== uncastPower || $[29] !== votingLabel) {
    t11 = typeof uncastPower === "number" && <span className="tw-rounded-full tw-border tw-border-solid tw-border-primary-500/30 tw-bg-primary-500/10 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-primary-300">{formatNumberWithCommas(uncastPower)} {votingLabel ?? "votes"} left</span>;
    $[28] = uncastPower;
    $[29] = votingLabel;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== remainingCount) {
    t12 = <span className="tw-rounded-full tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-iron-300">{remainingCount} left</span>;
    $[31] = remainingCount;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== isMobile || $[34] !== swipeHint) {
    t13 = isMobile && <span className="tw-rounded-full tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-iron-400">Swipe left to skip{swipeHint ? `, right to vote ${swipeHint}` : ""}</span>;
    $[33] = isMobile;
    $[34] = swipeHint;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== t11 || $[37] !== t12 || $[38] !== t13) {
    t14 = <div data-testid="quick-vote-preview-status" className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 md:tw-hidden">{t11}{t12}{t13}</div>;
    $[36] = t11;
    $[37] = t12;
    $[38] = t13;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== isMobile || $[41] !== swipeHint || $[42] !== swipeOffset) {
    t15 = isMobile && <><div className={clsx("tw-pointer-events-none tw-absolute tw-inset-y-0 tw-left-4 tw-z-10 tw-flex tw-items-center tw-text-sm tw-font-semibold tw-text-rose-300 tw-transition-opacity", swipeOffset < 0 ? "tw-opacity-100" : "tw-opacity-0")}>Skip</div><div className={clsx("tw-pointer-events-none tw-absolute tw-inset-y-0 tw-right-4 tw-z-10 tw-flex tw-items-center tw-text-right tw-text-sm tw-font-semibold tw-text-primary-300 tw-transition-opacity", swipeOffset > 0 ? "tw-opacity-100" : "tw-opacity-0")}>{swipeHint ? `Vote ${swipeHint}` : "Vote"}</div></>;
    $[40] = isMobile;
    $[41] = swipeHint;
    $[42] = swipeOffset;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  const t16 = isBusy && "tw-pointer-events-none tw-opacity-70";
  let t17;
  if ($[44] !== t16) {
    t17 = clsx("tw-relative tw-overflow-hidden tw-rounded-[2rem] tw-border tw-border-solid tw-border-white/10 tw-bg-iron-900/95 tw-shadow-[0_24px_60px_rgba(0,0,0,0.35)] tw-transition-all tw-duration-200 tw-ease-out", t16);
    $[44] = t16;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  const t18 = swipeExitDirection ? 0 : undefined;
  const t19 = isMobile ? "pan-y" : undefined;
  let t20;
  if ($[46] !== cardTransform || $[47] !== t18 || $[48] !== t19) {
    t20 = {
      transform: cardTransform,
      opacity: t18,
      touchAction: t19,
      transitionDuration: `${SWIPE_EXIT_DURATION_MS}ms`
    };
    $[46] = cardTransform;
    $[47] = t18;
    $[48] = t19;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  let t21;
  if ($[50] !== drop) {
    t21 = <WaveDropAuthorPfp drop={drop} />;
    $[50] = drop;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  let t22;
  if ($[52] !== authorLabel) {
    t22 = <span className="tw-truncate tw-text-sm tw-font-semibold tw-text-iron-100">{authorLabel}</span>;
    $[52] = authorLabel;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  let t23;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <span className="tw-size-1 tw-flex-shrink-0 tw-rounded-full tw-bg-iron-700" />;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== drop.created_at) {
    t24 = <span className="tw-text-xs tw-text-iron-500"><WaveDropTime timestamp={drop.created_at} /></span>;
    $[55] = drop.created_at;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  let t25;
  if ($[57] !== t22 || $[58] !== t24) {
    t25 = <div className="tw-flex tw-items-center tw-gap-2">{t22}{t23}{t24}</div>;
    $[57] = t22;
    $[58] = t24;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  let t26;
  if ($[60] !== drop.wave.name) {
    t26 = <p className="tw-mb-0 tw-mt-1 tw-truncate tw-text-xs tw-uppercase tw-tracking-[0.12em] tw-text-iron-500">{drop.wave.name}</p>;
    $[60] = drop.wave.name;
    $[61] = t26;
  } else {
    t26 = $[61];
  }
  let t27;
  if ($[62] !== t25 || $[63] !== t26) {
    t27 = <div className="tw-min-w-0 tw-flex-1">{t25}{t26}</div>;
    $[62] = t25;
    $[63] = t26;
    $[64] = t27;
  } else {
    t27 = $[64];
  }
  let t28;
  if ($[65] !== t21 || $[66] !== t27) {
    t28 = <div className="tw-border-b tw-border-solid tw-border-white/5 tw-p-4 md:tw-hidden"><div className="tw-flex tw-items-center tw-gap-3">{t21}{t27}</div></div>;
    $[65] = t21;
    $[66] = t27;
    $[67] = t28;
  } else {
    t28 = $[67];
  }
  let t29;
  if ($[68] !== title) {
    t29 = <h2 className="tw-mb-2 tw-text-2xl tw-font-semibold tw-leading-tight tw-text-white">{title}</h2>;
    $[68] = title;
    $[69] = t29;
  } else {
    t29 = $[69];
  }
  let t30;
  if ($[70] !== description) {
    t30 = description && <p className="tw-mb-4 tw-text-sm tw-leading-6 tw-text-iron-300" style={{
      display: "-webkit-box",
      WebkitBoxOrient: "vertical",
      WebkitLineClamp: 3,
      overflow: "hidden"
    }}>{description}</p>;
    $[70] = description;
    $[71] = t30;
  } else {
    t30 = $[71];
  }
  let t31;
  if ($[72] !== t29 || $[73] !== t30) {
    t31 = <div className="md:tw-hidden">{t29}{t30}</div>;
    $[72] = t29;
    $[73] = t30;
    $[74] = t31;
  } else {
    t31 = $[74];
  }
  let t32;
  if ($[75] !== artworkMedia) {
    t32 = artworkMedia ? <div className="tw-overflow-hidden tw-rounded-[1.5rem] tw-bg-iron-950 md:tw-rounded-none"><div className="tw-flex tw-h-[min(52vh,28rem)] tw-items-center tw-justify-center tw-bg-iron-950/80 md:tw-h-[min(72vh,44rem)] md:tw-bg-iron-950/85"><DropListItemContentMedia media_mime_type={artworkMedia.mime_type} media_url={artworkMedia.url} isCompetitionDrop={true} /></div></div> : <div className="tw-flex tw-h-64 tw-items-center tw-justify-center tw-rounded-[1.5rem] tw-border tw-border-dashed tw-border-iron-700 tw-bg-iron-950 tw-text-sm tw-text-iron-500 md:tw-h-[min(72vh,44rem)] md:tw-rounded-none md:tw-border-0">Preview unavailable</div>;
    $[75] = artworkMedia;
    $[76] = t32;
  } else {
    t32 = $[76];
  }
  let t33;
  if ($[77] !== t31 || $[78] !== t32) {
    t33 = <div data-testid="quick-vote-preview-mobile-context" className="tw-p-4 sm:tw-p-6 md:tw-p-0">{t31}{t32}</div>;
    $[77] = t31;
    $[78] = t32;
    $[79] = t33;
  } else {
    t33 = $[79];
  }
  let t34;
  if ($[80] !== handleTouchEnd || $[81] !== handleTouchMove || $[82] !== handleTouchStart || $[83] !== resetSwipe || $[84] !== t17 || $[85] !== t20 || $[86] !== t28 || $[87] !== t33) {
    t34 = <article data-testid="quick-vote-preview-card" className={t17} style={t20} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} onTouchCancel={resetSwipe}>{t28}{t33}</article>;
    $[80] = handleTouchEnd;
    $[81] = handleTouchMove;
    $[82] = handleTouchStart;
    $[83] = resetSwipe;
    $[84] = t17;
    $[85] = t20;
    $[86] = t28;
    $[87] = t33;
    $[88] = t34;
  } else {
    t34 = $[88];
  }
  let t35;
  if ($[89] !== t15 || $[90] !== t34) {
    t35 = <div className="tw-relative">{t15}{t34}</div>;
    $[89] = t15;
    $[90] = t34;
    $[91] = t35;
  } else {
    t35 = $[91];
  }
  let t36;
  if ($[92] !== t14 || $[93] !== t35) {
    t36 = <div className="tw-flex tw-flex-col tw-gap-4">{t14}{t35}</div>;
    $[92] = t14;
    $[93] = t35;
    $[94] = t36;
  } else {
    t36 = $[94];
  }
  return t36;
}
function _temp2(entry_0) {
  return entry_0.data_key === "description";
}
function _temp(entry) {
  return entry.data_key === "title";
}
export default function MemesQuickVotePreview(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <MemesQuickVotePreviewContent key={props.drop.id} {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}